/**
 * Client-side break stats for today's attendance record.
 */

export function hoursOfWorking(workingHours) {
  if (!workingHours) return 0;
  return (workingHours.hours || 0) + (workingHours.minutes || 0) / 60 + (workingHours.seconds || 0) / 3600;
}

export function resolveAttendanceAsOf(record, date, now = new Date()) {
  if (record?.checkOutTime) return new Date(record.checkOutTime);
  const d = date instanceof Date ? date : new Date(date);
  if (d.toDateString() === now.toDateString()) return now;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

/** Net working hours for display and full/half-day classification. */
export function resolveRecordWorkingHours(record, asOf = new Date()) {
  if (!record?.checkInTime) return 0;
  if (record.checkOutTime && record.workingHours) {
    const stored = hoursOfWorking(record.workingHours);
    if (stored > 0) return stored;
  }
  return getBreakStatsForRecord(record, asOf).netWorkingMs / 3600000;
}

export function getBreakStatsForRecord(record, now = new Date()) {
  if (!record?.checkInTime) {
    return {
      breaks: [],
      count: 0,
      totalBreakMs: 0,
      openBreak: null,
      onBreak: false,
      netWorkingMs: 0,
      grossMs: 0,
    };
  }

  const checkIn = new Date(record.checkInTime);
  let checkOut = record.checkOutTime ? new Date(record.checkOutTime) : now;
  // No checkout punched: for TODAY's active session show real elapsed time so
  // the live counter keeps rising past 9h and matches the eventual checkout
  // total. Only cap a PRIOR-day open record (a safety net for when the nightly
  // auto-close cron — checkin+9h — didn't run) so a stale punch can't inflate
  // hours into days. A real checkout is always shown as-is.
  if (!record.checkOutTime) {
    const isTodaySession = checkIn.toDateString() === now.toDateString();
    if (!isTodaySession) {
      const cap = new Date(checkIn.getTime() + 9 * 3600 * 1000);
      if (checkOut > cap) checkOut = cap;
    }
  }
  const grossMs = Math.max(0, checkOut - checkIn);

  const breakPunches = (record.additionalPunches || []).filter((p) => p.type === "break");
  const openBreak = breakPunches.find((p) => p.punchInAt && !p.punchOutAt) || null;

  let totalBreakMs = (record.totalBreakMinutes || 0) * 60000;
  if (openBreak?.punchInAt) {
    totalBreakMs += Math.max(0, now - new Date(openBreak.punchInAt));
  }

  const breaks = breakPunches
    .filter((p) => p.punchInAt)
    .map((p, index) => {
      const start = new Date(p.punchInAt);
      const end = p.punchOutAt ? new Date(p.punchOutAt) : null;
      const durationMs = end
        ? Math.max(0, end - start)
        : Math.max(0, now - start);
      return {
        id: p._id || `break-${index}`,
        start,
        end,
        durationMs,
        isOpen: !p.punchOutAt,
        label: p.label || "Break",
      };
    })
    .sort((a, b) => a.start - b.start);

  const netWorkingMs = Math.max(0, grossMs - totalBreakMs);

  return {
    breaks,
    count: breaks.length,
    totalBreakMs,
    openBreak,
    onBreak: !!openBreak,
    netWorkingMs,
    grossMs,
  };
}

export function fmtDurFromMs(ms) {
  if (!ms || ms < 0) return "0h 0m";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}
